/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: QueueAnnouncementsSection.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the PBX Call Center module.
----------------------------------------------------------------*/

import { useState, useEffect } from 'react';
import { Volume2 } from 'lucide-react';
import { useI18n } from '../../hooks/useI18n';
import { FormBox } from '../../components/ui/FormBox';
import { Label } from '../../components/ui/label';
import { Input } from '../../components/ui/input';
import SimpleTooltip from '../../components/ui/SimpleTooltip';
import RecordingSelect from '../../components/ui/RecordingSelect';
import { LanguageSelector } from '../extensions/components/LanguageSelector';
import { buildApiUrl, getApiHeaders } from '../../config/api';
import Toggle from '../../components/ui/Toggle';
import { FormState } from './types';

interface MOHSource {
  id: number;
  name: string;
  class_name: string;
  enabled: boolean;
}

interface QueueAnnouncementsSectionProps {
  formData: FormState;
  onFieldChange: <K extends keyof FormState>(field: K, value: FormState[K]) => void;
}

export function QueueAnnouncementsSection({
  formData,
  onFieldChange
}: QueueAnnouncementsSectionProps) {
  const { t } = useI18n();
  const [availableMOHSources, setAvailableMOHSources] = useState<MOHSource[]>([]);
  const [loadingMOHSources, setLoadingMOHSources] = useState(false);

  useEffect(() => {
    const loadMOHSources = async () => {
      setLoadingMOHSources(true);
      try {
        const response = await fetch(buildApiUrl('/api/telephony/moh-sources'), {
          headers: getApiHeaders()
        });
        if (response.ok) {
          const result = await response.json();
          if (result.success && Array.isArray(result.data)) {
            setAvailableMOHSources(result.data.filter((moh: MOHSource) => moh.enabled));
          }
        }
      } catch (error) {
        console.error('Error loading MOH sources:', error);
      } finally {
        setLoadingMOHSources(false);
      }
    };

    loadMOHSources();
  }, []);

  return (
    <FormBox title={t('queues.form.sections.announcements')} icon={<Volume2 className="h-5 w-5" />}>
      <div className="grid grid-cols-12 gap-3">
        {/* L�nea 1: Greeting y Language */}
        {/* Greeting */}
        <SimpleTooltip content={t('queues.form.fields.greeting.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="greeting" className="text-left">
            {t('queues.form.fields.greeting.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <RecordingSelect
            value={formData.greeting}
            onChange={(value) => onFieldChange('greeting', value || '')}
            placeholder={t('queues.form.fields.greeting.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        {/* Language */}
        <SimpleTooltip content={t('queues.form.fields.language.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="language" className="text-left">
            {t('queues.form.fields.language.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <LanguageSelector
            value={formData.language}
            onChange={(value) => onFieldChange('language', value)}
            showVoiceSelector={false}
            showLabel={false}
            disabled={false}
          />
        </div>

        {/* Welcome Sound */}
        <SimpleTooltip content={t('queues.form.fields.welcomeSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="welcomeSound" className="text-left">
            {t('queues.form.fields.welcomeSound.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <RecordingSelect
            value={formData.welcomeSound}
            onChange={(value) => onFieldChange('welcomeSound', value || '')}
            placeholder={t('queues.form.fields.welcomeSound.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        {/* Exit Sound */}
        <SimpleTooltip content={t('queues.form.fields.exitSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="exitSound" className="text-left">
            {t('queues.form.fields.exitSound.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <RecordingSelect
            value={formData.exitSound}
            onChange={(value) => onFieldChange('exitSound', value || '')}
            placeholder={t('queues.form.fields.exitSound.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        {/* L�nea 2: Agent Announcement y Music on Hold */}
        {/* Agent Announcement */}
        <SimpleTooltip content={t('queues.form.fields.agentConnectSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="agentConnectSound" className="text-left">
            {t('queues.form.fields.agentConnectSound.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <RecordingSelect
            value={formData.agentConnectSound}
            onChange={(value) => onFieldChange('agentConnectSound', value || '')}
            placeholder={t('queues.form.fields.agentConnectSound.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        {/* Music on Hold */}
        <SimpleTooltip content={t('queues.form.fields.musicOnHold.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="musicOnHold" className="text-left">
            {t('queues.form.fields.musicOnHold.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          {loadingMOHSources ? (
            <div className="text-sm text-muted-foreground">{t('common.loading')}</div>
          ) : (
            <select
              id="musicOnHold"
              value={formData.musicOnHold}
              onChange={(event) => onFieldChange('musicOnHold', event.target.value)}
              className="w-full rounded-md border border-border bg-background px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="local_stream://default">{t('queues.form.fields.musicOnHold.default')}</option>
              {availableMOHSources.map((moh) => (
                <option key={moh.id} value={`local_stream://${moh.class_name}`}>
                  {moh.name} ({moh.class_name})
                </option>
              ))}
            </select>
          )}
        </div>

        {/* L�nea 3: Periodic Announcement Sound y Periodic Announcement Frequency */}
        {/* Periodic Announcement Sound */}
        <SimpleTooltip content={t('queues.form.fields.announceSound.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="announceSound" className="text-left">
            {t('queues.form.fields.announceSound.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <RecordingSelect
            value={formData.announceSound}
            onChange={(value) => onFieldChange('announceSound', value || '')}
            placeholder={t('queues.form.fields.announceSound.placeholder')}
            returnType="id"
            showPlayButton
          />
        </div>

        {/* Periodic Announcement Frequency */}
        <SimpleTooltip content={t('queues.form.fields.announceFrequency.tooltip')} className="col-span-12 md:col-span-6 lg:col-span-3">
          <Label htmlFor="announceFrequency" className="text-left">
            {t('queues.form.fields.announceFrequency.label')}
          </Label>
        </SimpleTooltip>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Input
            id="announceFrequency"
            type="number"
            min="0"
            value={formData.announceFrequency}
            onChange={(event) => onFieldChange('announceFrequency', event.target.value)}
            placeholder={t('queues.form.fields.announceFrequency.placeholder')}
          />
        </div>

        {/* Announce Estimated Hold Time */}
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <SimpleTooltip content={t('queues.form.fields.announceEstimatedHoldTime.tooltip')}>
            <Label htmlFor="announceEstimatedHoldTime" className="text-left">
              {t('queues.form.fields.announceEstimatedHoldTime.label')}
            </Label>
          </SimpleTooltip>
        </div>
        <div className="col-span-12 md:col-span-6 lg:col-span-3">
          <Toggle
            id="announceEstimatedHoldTime"
            value={formData.announceEstimatedHoldTime}
            onChange={(value) => onFieldChange('announceEstimatedHoldTime', value)}
          />
        </div>
      </div>
    </FormBox>
  );
}

